import * as React from 'react'
import { compose, pure } from 'recompose'

import { QuestionType, QuizType } from '../../../../commons/EditorStatus'
import { ANSWER_COUNT } from '../../../../commons/QuizPhase'
import Button from '../../atoms/Button/Button'
import Spinner from '../../atoms/Spinner/Spinner'
import TextLabel, { ColorType, SizeType, WeightType } from '../../atoms/TextLabel/TextLabel'
import PresentationControllerStatus, {
  DirectionType
} from '../PresentationControllerStatus/PresentationControllerStatus'
import RealTimeAnswerChoice from '../RealTimeAnswerChoice/RealTimeAnswerChoice'
import * as styles from './PresentationController.css'

export enum ControllerType {
  NORMAL_FOR_DESKTOP,
  NORMAL_FOR_SMARTPHONE,
  HEADLESS_FOR_TABLET,
  HEADLESS_FOR_SMARTPHONE
}

interface PresentationControllerProps {
  index: number
  phase: number
  type: number
  questionType: number
  rankingCount: number
  isLoading?: boolean
  nowQuestion: any
  onChangeAnswerChoice: (data: any) => void
  controllerType: ControllerType
  handleBackButtonClick: () => void
  handleNextButtonClick: () => void
}

const PresentationController: React.FunctionComponent<PresentationControllerProps> = ({
  index,
  phase,
  type,
  questionType,
  rankingCount,
  isLoading = false,
  nowQuestion,
  onChangeAnswerChoice,
  controllerType,
  handleBackButtonClick,
  handleNextButtonClick
}) => {
  // 実演クイズでラジオボタンを表示する条件
  const showRadioButtons =
    questionType === QuestionType.REALTIME.value && type === QuizType.QUESTION.value && phase === ANSWER_COUNT
  let enableButtonClick
  if (showRadioButtons) {
    // 実演クイズなら、答えが決定されない限りボタンを押せない
    enableButtonClick = nowQuestion.answer_choice_id ? true : false
  } else {
    // 実演クイズでないなら、 isLoadingで見る
    enableButtonClick = !isLoading
  }
  const buttons = (
    <div className={styles.buttonGroup}>
      {showRadioButtons ? (
        <div className={styles.realTimeAnswerGroup}>
          <p className={styles.noticeText}>答えを選んでください</p>
          <div className={styles.realTimeAnswerChoice}>
            <RealTimeAnswerChoice nowQuestion={nowQuestion} onChangeAnswerChoice={onChangeAnswerChoice} />
          </div>
        </div>
      ) : null}
      {type === QuizType.NOTIFICATION.value ? null : (
        <div className={controllerType === ControllerType.HEADLESS_FOR_TABLET ? styles.miniButton : styles.button}>
          <Button handleClick={handleBackButtonClick} isEnabled={!isLoading}>
            <TextLabel color={ColorType.WHITE} size={SizeType.XTINY} weight={WeightType.LIGHT}>
              {controllerType === ControllerType.HEADLESS_FOR_TABLET ? '前へ' : '前のスライド'}
            </TextLabel>
          </Button>
        </div>
      )}
      <div className={controllerType === ControllerType.HEADLESS_FOR_TABLET ? styles.miniButton : styles.button}>
        <Button handleClick={handleNextButtonClick} isEnabled={enableButtonClick}>
          <TextLabel color={ColorType.WHITE} size={SizeType.XTINY} weight={WeightType.LIGHT}>
            {controllerType === ControllerType.HEADLESS_FOR_TABLET ? '次へ' : '次のスライド'}
          </TextLabel>
        </Button>
      </div>
    </div>
  )
  if (
    controllerType === ControllerType.HEADLESS_FOR_TABLET ||
    controllerType === ControllerType.HEADLESS_FOR_SMARTPHONE
  ) {
    return buttons
  }
  // コントローラーに出す文言と順番の制御
  let title = `第${index + 1}問`
  let highlightedIndex = phase
  switch (type) {
    case QuizType.NOTIFICATION.value:
      title = '注意書き'
      break
    case QuizType.FIRSTPAGE.value:
      title = '表紙'
      break
    case QuizType.RANKING.value:
      title = '結果発表'
      highlightedIndex = index
      break
    case QuizType.COMPLETE.value:
      title = 'クイズ終了'
      highlightedIndex = 0
      break
  }
  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <TextLabel>{title}</TextLabel>
        {isLoading ? (
          <div className={styles.loading}>
            <p className={styles.text}>
              {type === QuizType.RANKING.value && index === 0 ? 'ランキングを集計中' : '次のスライドを読み込み中'}
            </p>
            <div className={styles.spinner}>
              <Spinner />
            </div>
          </div>
        ) : null}
      </div>
      <div className={styles.controllerStatus}>
        <PresentationControllerStatus
          type={type}
          questionType={questionType}
          highlightedIndex={highlightedIndex}
          rankingCount={rankingCount}
          directionType={
            controllerType === ControllerType.NORMAL_FOR_SMARTPHONE ? DirectionType.vertical : DirectionType.horizontal
          }
        />
      </div>
      {type === QuizType.COMPLETE.value ? null : (
        <div className={styles.footerGroup}>
          {type === QuizType.RANKING.value && index === 0 ? (
            <p className={styles.noticeText}>発表画面の右下に集計完了と表示されるまで、次に進まずお待ちください</p>
          ) : null}
          {buttons}
        </div>
      )}
      <div className={styles.reloadNotice}>
        ※クイズが進まない場合は、操作パネルやクイズのスライドを再読み込みしてください
      </div>
    </div>
  )
}

export default compose<PresentationControllerProps, PresentationControllerProps>(pure)(PresentationController)
